/**
 * CHG-052 REQ-V52-008: 이슈 타입 스킴 관리 React Query 훅
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as schemeApi from '../services/issueTypeSchemeAdminService';
import type { CreateSchemeData, UpdateSchemeData } from '../services/issueTypeSchemeAdminService';
import { useOrgStore } from '../store/orgStore';

export const schemeAdminKeys = {
  all: (orgId?: string | null) => ['admin', 'issue-type-schemes', orgId ?? 'none'] as const,
  detail: (id: string) => ['admin', 'issue-type-schemes', 'detail', id] as const,
};

export function useSchemes() {
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  return useQuery({
    queryKey: schemeAdminKeys.all(activeOrgId),
    queryFn: schemeApi.fetchSchemes,
  });
}

export function useSchemeDetail(schemeId: string | undefined) {
  return useQuery({
    queryKey: schemeAdminKeys.detail(schemeId!),
    queryFn: () => schemeApi.fetchSchemeDetail(schemeId!),
    enabled: !!schemeId,
  });
}

export function useCreateScheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSchemeData) => schemeApi.createScheme(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'issue-type-schemes'] }),
  });
}

export function useUpdateScheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { schemeId: string; data: UpdateSchemeData }) =>
      schemeApi.updateScheme(params.schemeId, params.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'issue-type-schemes'] });
      qc.invalidateQueries({ queryKey: schemeAdminKeys.detail(vars.schemeId) });
    },
  });
}

export function useDeleteScheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (schemeId: string) => schemeApi.deleteScheme(schemeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'issue-type-schemes'] }),
  });
}

export function useCloneScheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (schemeId: string) => schemeApi.cloneScheme(schemeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'issue-type-schemes'] }),
  });
}
